import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
  Building2, 
  Download, 
  Filter, 
  Search,
  ChevronLeft,
  ChevronRight,
  Loader2,
  MapPin,
  FileText,
  Calendar,
  X,
  LogOut,
  ExternalLink,
  School,
  HardHat,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

// SCA Brand Colors from nycsca.org
const SCA_COLORS = {
  primary: '#003366',      // Dark Navy Blue
  secondary: '#0066cc',    // Medium Blue
  accent: '#ff6600',       // Orange
  light: '#f5f7fa',        // Light gray background
  text: '#333333',
  white: '#ffffff'
};

const BOROUGHS = ['MANHATTAN', 'BROOKLYN', 'BRONX', 'QUEENS', 'STATEN ISLAND'];
const JOB_TYPES = [
  { value: 'A1', label: 'Alteration Type 1 (Major)' },
  { value: 'A2', label: 'Alteration Type 2 (Minor)' },
  { value: 'A3', label: 'Alteration Type 3 (Cosmetic)' },
  { value: 'NB', label: 'New Building' },
  { value: 'DM', label: 'Demolition' },
];

export default function SCAPortalPage() {
  const { api, user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit] = useState(50);
  const [stats, setStats] = useState({ byBorough: {}, byType: {} });
  
  // Filters
  const [borough, setBorough] = useState('');
  const [jobType, setJobType] = useState('');
  const [status, setStatus] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Check access - only jh@ allowed
  useEffect(() => {
    if (user && !user.email?.startsWith('jh@')) {
      toast.error('Access denied. This portal is restricted.');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', limit);
      params.append('offset', page * limit);
      if (borough) params.append('borough', borough);
      if (jobType) params.append('job_type', jobType);
      if (status) params.append('status', status);
      
      const res = await api().get(`/export/custom/school-construction-authority?${params.toString()}`);
      setData(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch SCA data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [api, page, limit, borough, jobType, status]);

  // Fetch borough stats
  const fetchStats = useCallback(async () => {
    try {
      const boroughStats = {};
      for (const b of BOROUGHS) {
        const res = await api().get(`/export/custom/school-construction-authority?limit=1&borough=${encodeURIComponent(b)}`);
        boroughStats[b] = res.data.total || 0;
      }
      setStats(prev => ({ ...prev, byBorough: boroughStats }));
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', '5000');
      if (borough) params.append('borough', borough);
      if (jobType) params.append('job_type', jobType);
      if (status) params.append('status', status);
      
      const res = await api().get(`/export/custom/school-construction-authority/export?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sca_filings_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export downloaded successfully');
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setBorough('');
    setJobType('');
    setStatus('');
    setSearchQuery('');
    setPage(0);
  };

  const hasFilters = borough || jobType || status || searchQuery;
  const totalPages = Math.ceil(total / limit);

  // Filter data by search query
  const filteredData = searchQuery 
    ? data.filter(rec => 
        rec.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        rec.job_number?.includes(searchQuery)
      )
    : data;

  return (
    <div className="min-h-screen" style={{ backgroundColor: SCA_COLORS.light }}>
      {/* SCA Header */}
      <header style={{ backgroundColor: SCA_COLORS.primary }} className="text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* SCA Logo/Branding */}
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: SCA_COLORS.accent }}>
                  <School className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold tracking-tight">NYC School Construction Authority</h1>
                  <p className="text-xs opacity-80">Building Permits & Filings Database</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <a 
                href="https://www.nycsca.org/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm opacity-80 hover:opacity-100 flex items-center gap-1"
              >
                NYCSCA.org <ExternalLink className="w-3 h-3" />
              </a>
              <span className="text-sm opacity-60">|</span>
              <span className="text-sm opacity-80">{user?.name || user?.email}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { logout(); navigate('/'); }}
                className="text-white/80 hover:text-white hover:bg-white/10"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
        
        {/* Orange accent bar */}
        <div style={{ backgroundColor: SCA_COLORS.accent, height: '4px' }}></div>
      </header>

      {/* Stats Banner */}
      <div style={{ backgroundColor: SCA_COLORS.secondary }} className="text-white py-4">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
            {BOROUGHS.map(b => (
              <div key={b} className="bg-white/10 rounded-lg py-2 px-3">
                <div className="text-lg font-bold">{(stats.byBorough[b] || 0).toLocaleString()}</div>
                <div className="text-xs opacity-80">{b}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* Action Bar */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="relative flex-1 md:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by address or job number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className={hasFilters ? 'border-orange-500 text-orange-600' : ''}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                {hasFilters && <span className="ml-2 bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded">ON</span>}
              </Button>
            </div>
            
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">
                <strong className="text-gray-900">{total.toLocaleString()}</strong> total records
              </span>
              
              <Button
                onClick={handleExport}
                disabled={exporting || total === 0}
                style={{ backgroundColor: SCA_COLORS.accent }}
                className="text-white hover:opacity-90"
              >
                {exporting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Export CSV
              </Button>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-700">Filter Results</h3>
                {hasFilters && (
                  <button onClick={clearFilters} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
                    <X className="w-3 h-3" /> Clear all
                  </button>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Borough</label>
                  <select
                    value={borough}
                    onChange={(e) => { setBorough(e.target.value); setPage(0); }}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Boroughs</option>
                    {BOROUGHS.map(b => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Job Type</label>
                  <select
                    value={jobType}
                    onChange={(e) => { setJobType(e.target.value); setPage(0); }}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Types</option>
                    {JOB_TYPES.map(jt => (
                      <option key={jt.value} value={jt.value}>{jt.label}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Status</label>
                  <select
                    value={status}
                    onChange={(e) => { setStatus(e.target.value); setPage(0); }}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Statuses</option>
                    <option value="PERMIT ISSUED">Permit Issued</option>
                    <option value="SIGNED OFF">Signed Off</option>
                    <option value="IN PROCESS">In Process</option>
                    <option value="APPROVED">Approved</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: SCA_COLORS.secondary }} />
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-20 text-gray-500">
              <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No records found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ backgroundColor: SCA_COLORS.primary }} className="text-left text-white">
                    <th className="px-4 py-3 font-medium">Job #</th>
                    <th className="px-4 py-3 font-medium">Address</th>
                    <th className="px-4 py-3 font-medium">Borough</th>
                    <th className="px-4 py-3 font-medium">Block/Lot</th>
                    <th className="px-4 py-3 font-medium">Type</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Last Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredData.map((rec, idx) => (
                    <tr key={`${rec.job_number}-${idx}`} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs" style={{ color: SCA_COLORS.secondary }}>{rec.job_number}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-900">{rec.address || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{rec.borough}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {rec.block}/{rec.lot}
                      </td>
                      <td className="px-4 py-3">
                        <span 
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs text-white"
                          style={{ backgroundColor: SCA_COLORS.secondary }}
                        >
                          <HardHat className="w-3 h-3" />
                          {rec.job_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                          rec.job_status_description?.includes('SIGNED OFF') 
                            ? 'bg-green-100 text-green-700'
                            : rec.job_status_description?.includes('PERMIT ISSUED')
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {rec.job_status_description || rec.job_status || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-gray-500 text-xs">
                          <Calendar className="w-3 h-3" />
                          {rec.latest_action_date 
                            ? new Date(rec.latest_action_date).toLocaleDateString() 
                            : 'N/A'}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t">
              <span className="text-sm text-gray-500">
                Page {page + 1} of {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0 || loading}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1 || loading}
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer style={{ backgroundColor: SCA_COLORS.primary }} className="text-white mt-8 py-6">
        <div className="max-w-7xl mx-auto px-6 text-center text-sm opacity-80">
          <p>Data sourced from NYC Open Data • Building Permits & Job Filings</p>
          <p className="mt-1">
            <a href="https://www.nycsca.org/" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-100">
              NYC School Construction Authority
            </a>
            {' '} • Powered by Insite360
          </p>
        </div>
      </footer>
    </div>
  );
}
